import { Injectable } from '@angular/core';
import { ChartFacade } from '@mode/charts/contract';
import { DataViewFacade } from '@mode/switch-views/contract';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';

import { DataViewTypes, ReportTypes, UserResourceTypes } from '@mode/shared/contract-common';
import { filter, first, map, shareReplay, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import * as ReportDbtMetaData from '../../../domain/src/lib/report-dbt-metadata/report-dbt-metadata.actions';
import * as ReportViewerActions from '../../../domain/src/lib/report-viewer/report-viewer.actions';
import {
  ReportViewerDomainState,
  chartTokensInLayoutGf,
  getAccountUsername,
  getActiveToastId,
  getAreFormFieldsDynamic,
  getCreatedAt,
  getCreator,
  getDescription,
  getExpectedRuntime,
  getFormFields,
  getFullWidth,
  getHasPublished,
  getHasRecentReportRun,
  getHasRun,
  getIframeSrc,
  getIsLatestReportRun,
  getIsLatestSuccessfulReportRun,
  getLastSuccessfullyRunAt,
  getLayout,
  getManualRunDisabled,
  getMostRecentReportRunState,
  getMostRecentReportRunToken,
  getName,
  getParentReportLink,
  getParentReportName,
  getReportHasFailuresSinceLastSuccess,
  getReportRunCompletedAt,
  getReportRunState,
  getReportRunToken,
  getShowFilterHeader,
  getShowReportHistory,
  getTheme,
  getToken,
  getWebLink,
  hasScriptTagGf,
  isReportBuilderEmpty,
  tableTokensInLayoutGf,
} from './report-viewer/report-viewer.selectors';
import { FLAMINGO_RESERVED_REFS, isPresent } from '@mode/shared/util-js';
import { DatasetFacade } from '@mode/dataset/contract';

@Injectable()
export class ReportViewerFacade {
  constructor(
    private store: Store<ReportViewerDomainState>,
    private chartFacade: ChartFacade,
    private dataViewFacade: DataViewFacade,
    private datasetFacade: DatasetFacade
  ) {
    // Report info
    this.expectedRuntime$ = store.select(getExpectedRuntime).pipe(shareReplay(1));
    this.hasPublished$ = store.select(getHasPublished).pipe(shareReplay(1));
    this.canSeeHistory$ = store.select(getShowReportHistory).pipe(shareReplay(1));
    this.createdAt$ = store.select(getCreatedAt).pipe(shareReplay(1));
    this.creator$ = store.select(getCreator).pipe(filter(isPresent), shareReplay(1));
    this.description$ = store.select(getDescription).pipe(shareReplay(1));
    this.displayName$ = store.select(getName).pipe(shareReplay(1));
    this.webLink$ = store.select(getWebLink).pipe(shareReplay(1));
    this.iframeSrc$ = store.select(getIframeSrc).pipe(shareReplay(1));
    this.isFullWidth$ = store.select(getFullWidth).pipe(filter(isPresent), shareReplay(1));
    this.themeNoEmpty$ = store.select(getTheme).pipe(filter(isPresent), shareReplay(1));
    this.token$ = store.select(getToken).pipe(filter(isPresent), shareReplay(1));

    // Layout
    this.chartTokensInLayout$ = this.store.select(chartTokensInLayoutGf).pipe(filter(isPresent), shareReplay(1));
    this.isReportBuilderEmpty$ = store.select(isReportBuilderEmpty).pipe(shareReplay(1));
    this.reportLayoutNoEmpty$ = store.select(getLayout).pipe(filter(isPresent), shareReplay(1));
    this.v2ChartTokensInLayout$ = combineLatest([this.chartTokensInLayout$, this.chartFacade.v2ChartTokens$]).pipe(
      map(([layoutTokens, chartTokens]) => layoutTokens.filter((t) => chartTokens.has(t))),
      shareReplay(1)
    );
    this.v1ChartTokensInLayout$ = combineLatest([this.chartTokensInLayout$, this.chartFacade.v1ChartTokens$]).pipe(
      map(([layoutTokens, chartTokens]) => layoutTokens.filter((t) => chartTokens.has(t))),
      shareReplay(1)
    );
    this.hasScriptTagGf$ = store.select(hasScriptTagGf).pipe(shareReplay(1));
    this.tableTokensInLayout$ = store.select(tableTokensInLayoutGf).pipe(shareReplay(1));
    this.v1ChartsInLayoutCount$ = this.v1ChartTokensInLayout$.pipe(map((tokens) => tokens?.length || 0));
    this.v2ChartsInLayoutCount$ = this.v2ChartTokensInLayout$.pipe(map((tokens) => tokens?.length || 0));
    this.tablesInLayoutCount$ = this.tableTokensInLayout$.pipe(map((tables) => tables?.length || 0));
    this.iframeLoaded$ = new BehaviorSubject<boolean>(false);
    this.activeToastId$ = store.select(getActiveToastId).pipe(shareReplay(1));
    // Filters and Parameters
    this.showFilterHeader$ = store.select(getShowFilterHeader).pipe(shareReplay(1));
    this.reportRunFormFields$ = store.select(getFormFields).pipe(filter(isPresent), shareReplay(1));
    this.areReportRunFormFieldsDynamic$ = store.select(getAreFormFieldsDynamic).pipe(filter(isPresent), shareReplay(1));
    this.showParamsHeader$ = this.reportRunFormFields$.pipe(map((fields) => fields.length > 0));

    // Report run context
    this.hasRun$ = store.select(getHasRun).pipe(shareReplay(1));
    this.reportRunCompletedAt$ = store.select(getReportRunCompletedAt).pipe(filter(isPresent), shareReplay(1));
    /**
     * TODO: reduce new report run status into state
     * https://github.com/mode/issues/issues/13051
     *
     * For now, this should be treated as the report's inital run state at page load time as
     * reportRunState$ does not update when the run status changes
     */
    this.reportRunState$ = store.select(getReportRunState).pipe(filter(isPresent), shareReplay(1));
    this.reportRunToken$ = store.select(getReportRunToken).pipe(filter(isPresent), shareReplay(1));
    this.isLatestReportRun$ = store.select(getIsLatestReportRun).pipe(filter(isPresent), shareReplay(1));
    this.hasRecentReportRun$ = store.select(getHasRecentReportRun).pipe(filter(isPresent), shareReplay(1));
    this.manualRunDisabled$ = store.select(getManualRunDisabled).pipe(shareReplay(1));
    this.mostRecentReportRunToken$ = store.select(getMostRecentReportRunToken).pipe(filter(isPresent), shareReplay(1));
    this.mostRecentReportRunState$ = store.select(getMostRecentReportRunState).pipe(filter(isPresent), shareReplay(1));
    this.lastSuccessfullyRunAt$ = store.select(getLastSuccessfullyRunAt).pipe(shareReplay(1));
    this.parentReportName$ = store.select(getParentReportName).pipe(shareReplay(1));
    this.parentReportLink$ = store.select(getParentReportLink).pipe(shareReplay(1));
    this.reportAccountUsername$ = store.select(getAccountUsername).pipe(shareReplay(1));
    this.reportHasFailuresSinceLastSuccess$ = store
      .select(getReportHasFailuresSinceLastSuccess)
      .pipe(filter(isPresent), shareReplay(1));
    this.isLatestSuccessfulReportRun$ = store
      .select(getIsLatestSuccessfulReportRun)
      .pipe(filter(isPresent), shareReplay(1));

    this.mostRecentReportRunLink$ = this.mostRecentReportRunToken$.pipe(
      withLatestFrom(this.webLink$),
      map(([runToken, webLink]) => `${webLink}/runs/${runToken}`)
    );

    this.reportRunDetailsLink$ = this.mostRecentReportRunToken$.pipe(
      withLatestFrom(this.webLink$),
      map(([reportRunToken, webLink]) => `${webLink}/runs/${reportRunToken}/details`)
    );

    this.reportRunErrorLink$ = this.dataViewFacade.datasetResults$.pipe(
      map((datasetResults) =>
        datasetResults.filter((results) => results.queryRunState === DataViewTypes.QueryRunStatus.Failed)
      ),
      filter((failedDatasetResults) => !!failedDatasetResults.length),
      // alphabetize results by query name to get the first failed query that appears in the left sidebar
      map((failedDatasetResults) => failedDatasetResults.sort((a, b) => a.queryName.localeCompare(b.queryName))),
      withLatestFrom(this.webLink$, this.reportRunToken$),
      map(([failedDatasetResults, webLink, reportRunToken]) => {
        let queryToken = failedDatasetResults[0].queryToken;
        return `${webLink}/runs/${reportRunToken}/details/queries/${queryToken}/data`;
      })
    );

    this.reportRunBannerType$ = combineLatest([
      this.isLatestSuccessfulReportRun$,
      this.isLatestReportRun$,
      this.reportRunState$,
      this.lastSuccessfullyRunAt$,
      this.reportRunCompletedAt$,
      this.mostRecentReportRunState$,
    ]).pipe(
      map(
        ([
          isLatestSuccessfulReportRun,
          isLatestReportRun,
          reportRunState,
          lastSuccessfullyRunAt,
          reportRunCompletedAt,
          mostRecentReportRunState,
        ]) => {
          // If current run is most recent run and it succeeded, don't show a banner
          if (isLatestReportRun && isLatestSuccessfulReportRun) {
            return ReportTypes.ReportRunBannerTypes.NoBanner;
          }
          // Current run failed, and there's no newer success
          if (reportRunState === DataViewTypes.ReportRunStatus.Failed && lastSuccessfullyRunAt < reportRunCompletedAt) {
            return ReportTypes.ReportRunBannerTypes.FailedRunWithoutNewerSuccess;
          }
          // Current run is not the most recent, but report has failures since last success
          if (
            isLatestSuccessfulReportRun &&
            !isLatestReportRun &&
            mostRecentReportRunState === DataViewTypes.ReportRunStatus.Failed
          ) {
            return ReportTypes.ReportRunBannerTypes.LastSuccessfulRunWithNewerFailure;
          }
          // Current run failed, but there is a newer success
          if (reportRunState === DataViewTypes.ReportRunStatus.Failed && lastSuccessfullyRunAt > reportRunCompletedAt) {
            return ReportTypes.ReportRunBannerTypes.FailedRunWithNewerSuccess;
          }
          // Current run succeeded, but there is a newer success
          if (reportRunState === DataViewTypes.ReportRunStatus.Succeeded && !isLatestSuccessfulReportRun) {
            return ReportTypes.ReportRunBannerTypes.SuccessfulRunWithNewerSuccess;
          }

          // In all other cases, don't show a banner
          return ReportTypes.ReportRunBannerTypes.NoBanner;
        }
      )
    );
  }

  readonly tableTokensInLayout$: Observable<any>;
  readonly chartTokensInLayout$: Observable<ReadonlyArray<string>>;
  readonly v2ChartTokensInLayout$: Observable<ReadonlyArray<string>>;
  readonly v1ChartTokensInLayout$: Observable<ReadonlyArray<string>>;
  readonly v1ChartsInLayoutCount$: Observable<number>;
  readonly v2ChartsInLayoutCount$: Observable<number>;
  readonly tablesInLayoutCount$: Observable<number>;
  readonly isReportBuilderEmpty$: Observable<boolean>;
  readonly showFilterHeader$: Observable<boolean>;
  readonly reportRunFormFields$: Observable<ReportTypes.ReportRunFormField[]>;
  readonly areReportRunFormFieldsDynamic$: Observable<boolean>;
  readonly showParamsHeader$: Observable<boolean>;
  readonly hasRun$: Observable<boolean>;
  readonly reportRunCompletedAt$: Observable<Date>;
  readonly reportRunToken$: Observable<string>;
  readonly reportHasFailuresSinceLastSuccess$: Observable<boolean>;
  readonly isLatestReportRun$: Observable<boolean>;
  readonly hasRecentReportRun$: Observable<boolean>;
  readonly isLatestSuccessfulReportRun$: Observable<boolean>;
  readonly reportRunState$: Observable<DataViewTypes.ReportRunStatus>;
  readonly reportRunBannerType$: Observable<ReportTypes.ReportRunBannerType>;
  readonly manualRunDisabled$: Observable<boolean>;
  readonly mostRecentReportRunToken$: Observable<string>;
  readonly mostRecentReportRunState$: Observable<string>;
  readonly expectedRuntime$: Observable<number>;
  readonly hasPublished$: Observable<boolean>;
  readonly canSeeHistory$: Observable<boolean>;
  readonly parentReportName$: Observable<string>;
  readonly parentReportLink$: Observable<string>;
  readonly reportAccountUsername$: Observable<string>;
  readonly creator$: Observable<UserResourceTypes.UserResource>;
  readonly createdAt$: Observable<string>;
  readonly description$: Observable<string>;
  readonly displayName$: Observable<string>;
  readonly lastSuccessfullyRunAt$: Observable<Date>;
  readonly reportLayoutNoEmpty$: Observable<string>;
  readonly themeNoEmpty$: Observable<ReportTypes.ReportTheme>;
  readonly iframeSrc$: Observable<string>;
  readonly webLink$: Observable<string>;
  readonly reportRunDetailsLink$: Observable<string>;
  readonly reportRunErrorLink$: Observable<string>;
  readonly mostRecentReportRunLink$: Observable<string>;
  readonly isFullWidth$: Observable<boolean>;
  readonly token$: Observable<string>;
  readonly hasScriptTagGf$: Observable<boolean>;
  readonly iframeLoaded$: BehaviorSubject<boolean>;
  readonly activeToastId$: Observable<number | undefined>;
  /**
   * Start the process to load a report
   */
  selectReport(accountId: string, reportToken: string, reportRunToken: string | null) {
    this.store.dispatch(
      ReportViewerActions.loadFullReport({
        accountId,
        reportToken,
        reportRunToken,
      })
    );
    this.store.dispatch(
      ReportViewerActions.loadReportViewVisualizations({
        accountId,
        reportToken,
      })
    );
    this.store.dispatch(
      ReportDbtMetaData.loadReportDbtMetadata({
        accountId,
        reportToken,
      })
    );
  }

  loadReportViewVisualizationsFromExploration(accountId: string, reportToken: string) {
    combineLatest([this.chartFacade.selectedToken$, this.chartFacade.chartTokens$])
      .pipe(
        first(),
        map(([selectedToken, chartTokens]) => {
          /**
           * When chartTokens has only 2 items and the selectedToken is explorer-chart.
           * chartTokens contains the vizToken of the exploration and FLAMINGO_RESERVED_REFS identifier - explorer-chart
           * Therefore request additional chart resources in order to render report
           */
          if (selectedToken === FLAMINGO_RESERVED_REFS.ChartExplorer && chartTokens.size === 2) {
            this.store.dispatch(
              ReportViewerActions.loadReportViewVisualizationsFromExploration({
                accountId,
                reportToken,
              })
            );
          }
        })
      )
      .subscribe();
  }

  loadQueryRun(accountId: string, reportToken: string, reportRunToken: string, queryId: string) {
    this.dataViewFacade
      .getDatasetResultByQuery(queryId)
      .pipe(
        first(),
        map((dataset) => this.datasetFacade.isSheetDataset(dataset?.producerType ?? null)),
        switchMap((isSheetDataset) => {
          return isSheetDataset
            ? of(undefined)
            : this.dataViewFacade.createQueryRunTokenByQueryToken(queryId).pipe(
                filter(isPresent),
                first(),
                tap((queryRunToken: string) =>
                  this.store.dispatch(
                    ReportViewerActions.loadQueryRun({
                      accountId,
                      reportToken,
                      reportRunToken,
                      queryRunToken,
                    })
                  )
                )
              );
        })
      )
      .subscribe();
  }

  /**
   * New report run polling for full screen mode.
   */
  pollForNewReportRuns(
    accountId: string,
    reportToken: string,
    reportRunToken: string,
    onNewReportRun: (newReport: any) => void
  ) {
    this.store.dispatch(
      ReportViewerActions.pollForNewReportRuns({
        accountId,
        reportToken,
        reportRunToken,
        onNewReportRun,
      })
    );
  }

  reportViewRouteLoadReportRun(accountId: string, reportToken: string, reportRunToken?: string) {
    this.store.dispatch(
      ReportViewerActions.reportViewRouteLoadReportView({
        accountId,
        reportToken,
        reportRunToken,
      })
    );
  }

  iframeLoaded(isIframeLoaded: boolean) {
    this.iframeLoaded$.next(isIframeLoaded);
  }

  showLoadingToast() {
    this.store.dispatch(ReportViewerActions.showLoadingToast());
  }
  hideLoadingToast(toastId: number) {
    this.store.dispatch(ReportViewerActions.hideLoadingToast({ toastId }));
  }
}
